/* ==========================================================================
   e3-obsidian.js — site-wide geo-obsidian wall
   Acquires WebGL, and ONLY on success adds html.e3-obsidian-on, which is what
   opens the transparency windows in e3-obsidian.css. Any failure (no WebGL,
   compile error, blocked script) leaves the site exactly as it is today.
   ========================================================================== */
(function () {
  "use strict";

  if (window.__e3Obsidian) return;                    // single-instance guard
  var canvas = document.getElementById("e3-obsidian");
  if (!canvas) return;

  var REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;
  var TOUCH   = matchMedia("(hover: none)").matches;
  var ANIM    = !REDUCED;                             // loop runs on desktop AND touch
  var POINTER = !REDUCED && !TOUCH;                   // cursor light + parallax
  var DPR     = Math.min(window.devicePixelRatio || 1, 2);

  /* locked constants (approved prototype) */
  var CELL_PX = 100, SWEEP = 0.30, GLINT = 0.20, PAR_X = -0.22, PAR_Y = -0.15;

  var VS = "attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}";

  /* Jittered-grid DELAUNAY facets (in-circle diagonal) resolved by a 3x3
     neighbour argmax, so facet edges are true Delaunay edges at arbitrary
     angles => NO axis-aligned square lattice. Flat per-facet normal from the
     three lifted sites. Facet size is a fixed pixel constant (uCell), so a
     bigger viewport shows MORE facets and never upscales. WebGL1/GLSL ES 1.00. */
  var FS = [
    "#ifdef GL_FRAGMENT_PRECISION_HIGH",
    "precision highp float;",
    "#else",
    "precision mediump float;",
    "#endif",
    "uniform vec2 uRes, uMouse;",
    "uniform float uTime, uInter, uCell, uSweep, uGlint, uPx, uPy;",
    "const float JIT=0.38;",
    "const float RELIEF=0.55;",
    "const vec3 DARK=vec3(4.0,6.0,11.0)/255.0;",
    "const vec3 BASE=vec3(30.0,40.0,60.0)/255.0;",
    "const vec3 SPEC=vec3(0.62,0.73,0.92);",
    "float h1(vec2 p){ return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453123); }",
    "vec2 h2(vec2 p){ return fract(sin(vec2(dot(p,vec2(127.1,311.7)),dot(p,vec2(269.5,183.3))))*43758.5453123); }",
    "vec3 site3(vec2 c){",
    "  vec2 j=(h2(c)-0.5)*2.0*JIT;",
    "  float h=h1(c+vec2(31.4,17.9));",
    "  return vec3(c+j,(h-0.5)*RELIEF);",
    "}",
    "float incircle(vec2 a,vec2 b,vec2 c,vec2 d){",
    "  vec2 ad=a-d,bd=b-d,cd=c-d;",
    "  float a2=dot(ad,ad),b2=dot(bd,bd),c2=dot(cd,cd);",
    "  return ad.x*(bd.y*c2-b2*cd.y)-ad.y*(bd.x*c2-b2*cd.x)+a2*(bd.x*cd.y-bd.y*cd.x);",
    "}",
    "void consider(vec2 P,vec3 a,vec3 b,vec3 c,inout float best,inout vec3 A,inout vec3 B,inout vec3 C){",
    "  vec2 pa=a.xy,pb=b.xy,pc=c.xy;",
    "  float area2=(pb.x-pa.x)*(pc.y-pa.y)-(pb.y-pa.y)*(pc.x-pa.x);",
    "  if(abs(area2)<1e-6) return;",
    "  float inv=1.0/area2;",
    "  float e0=((pb.x-pa.x)*(P.y-pa.y)-(pb.y-pa.y)*(P.x-pa.x))*inv;",
    "  float e1=((pc.x-pb.x)*(P.y-pb.y)-(pc.y-pb.y)*(P.x-pb.x))*inv;",
    "  float e2=((pa.x-pc.x)*(P.y-pc.y)-(pa.y-pc.y)*(P.x-pc.x))*inv;",
    "  float sc=min(e0,min(e1,e2));",
    "  if(sc>best){ best=sc; A=a; B=b; C=c; }",
    "}",
    "void main(){",
    "  vec2 P=gl_FragCoord.xy/uCell+vec2(uPx,uPy);",
    "  vec2 g=floor(P);",
    "  vec3 bA=vec3(0.0),bB=vec3(0.0),bC=vec3(0.0);",
    "  float best=-1e18;",
    "  for(int gy=-1; gy<=1; gy++){",
    "    for(int gx=-1; gx<=1; gx++){",
    "      vec2 gg=g+vec2(float(gx),float(gy));",
    "      vec3 A=site3(gg), B=site3(gg+vec2(1.0,0.0)), C=site3(gg+vec2(1.0,1.0)), D=site3(gg+vec2(0.0,1.0));",
    "      float inc=incircle(A.xy,B.xy,C.xy,D.xy);",
    "      if(inc<=0.0){ consider(P,A,B,C,best,bA,bB,bC); consider(P,A,C,D,best,bA,bB,bC); }",
    "      else { consider(P,A,B,D,best,bA,bB,bC); consider(P,B,C,D,best,bA,bB,bC); }",
    "    }",
    "  }",
    "  vec3 nv=cross(bB-bA,bC-bA);",
    "  if(nv.z<0.0) nv=-nv;",
    "  vec3 N=normalize(nv+vec3(0.0,0.0,1e-4));",
    "  vec3 cen=(bA+bB+bC)/3.0;",
    "  float ct=h1(cen.xy*1.7+3.1);",
    "  float ang=uTime*uSweep;",
    "  vec3 L=normalize(vec3(0.42*sin(ang),0.32*cos(ang*0.85)+0.06,0.86));",
    "  float nl=max(dot(N,L),0.0);",
    "  float spec=pow(nl,10.0)*0.22*uGlint;",
    "  vec2 M=uMouse/uCell+vec2(uPx,uPy);",
    "  vec2 toM=M-P;",
    "  float md=length(toM);",
    "  vec3 Lc=normalize(vec3(toM,1.2));",
    "  float cdiff=max(dot(N,Lc),0.0);",
    "  float cursor=cdiff*uInter*exp(-md*md*0.02)*0.8;",
    "  float lit=0.13+nl*0.75+(ct-0.5)*0.10+cursor*0.5;",
    "  vec3 col=mix(DARK,BASE,clamp(lit,0.0,1.0));",
    "  col+=SPEC*spec;",
    "  col+=SPEC*cursor*0.35;",
    "  vec2 uv=gl_FragCoord.xy/uRes;",
    "  float vg=smoothstep(1.25,0.35,length(uv-vec2(0.5,0.55)));",
    "  col*=mix(0.80,1.0,vg);",
    "  gl_FragColor=vec4(clamp(col,0.0,1.0),1.0);",
    "}"
  ].join("\n");

  var CTX = { antialias: false, alpha: false, powerPreference: "high-performance" };
  var gl = null, prog = null, U = {}, raf = null, t0 = null, last = null;
  var lastW = 0, lastH = 0, rTimer = null;

  var M = { x: 0, y: 0, tx: 0, ty: 0 };   // cursor, DEVICE px, gl_FragCoord origin
  var P = { x: 0, y: 0, tx: 0, ty: 0 };   // parallax: .x/.y = uniform, .tx/.ty = target
  var S = { px: 0, py: 0 };               // smoothed pointer

  function compile(type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      console.error("[e3-obsidian] shader compile:", gl.getShaderInfoLog(s));
      return null;
    }
    return s;
  }

  /* Build (or rebuild, after context restore) all GL state. Returns bool. */
  function initGL() {
    gl = canvas.getContext("webgl", CTX) || canvas.getContext("experimental-webgl", CTX);
    if (!gl) return false;

    var vs = compile(gl.VERTEX_SHADER, VS);
    var fs = compile(gl.FRAGMENT_SHADER, FS);
    if (!vs || !fs) return false;

    prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.error("[e3-obsidian] link:", gl.getProgramInfoLog(prog));
      return false;
    }
    gl.useProgram(prog);

    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(prog, "p");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    U = {};
    ["uRes", "uMouse", "uTime", "uInter", "uCell", "uSweep", "uGlint", "uPx", "uPy"]
      .forEach(function (n) { U[n] = gl.getUniformLocation(prog, n); });

    size();
    return true;
  }

  function size() {
    canvas.width  = Math.round(window.innerWidth  * DPR);
    canvas.height = Math.round(window.innerHeight * DPR);
    gl.viewport(0, 0, canvas.width, canvas.height);
    if (!M.x && !M.y) {
      M.x = M.tx = canvas.width * 0.5;
      M.y = M.ty = canvas.height * 0.55;
    }
    if (!ANIM) draw(6.0);
  }

  /* Ignore mobile URL-bar show/hide (height-only delta) to avoid constant
     backing-store reallocation. */
  function onResize() {
    if (window.innerWidth === lastW && Math.abs(window.innerHeight - lastH) < 120) return;
    clearTimeout(rTimer);
    rTimer = setTimeout(function () {
      lastW = window.innerWidth; lastH = window.innerHeight; size();
    }, 150);
  }

  function settle(c, t, f) { var n = c + (t - c) * f; return Math.abs(t - n) < 1e-4 ? t : n; }
  var K  = function (p) { return -Math.log(1 - p) / (1000 / 60); };
  var PK = K(0.05), MK = K(0.10);

  function draw(t) {
    gl.uniform2f(U.uRes, canvas.width, canvas.height);
    gl.uniform2f(U.uMouse, M.x, M.y);
    gl.uniform1f(U.uTime, t);
    gl.uniform1f(U.uInter, POINTER ? 1 : 0);
    gl.uniform1f(U.uCell, CELL_PX * DPR);
    gl.uniform1f(U.uSweep, SWEEP);
    gl.uniform1f(U.uGlint, GLINT);
    gl.uniform1f(U.uPx, P.x);
    gl.uniform1f(U.uPy, P.y);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  function loop(now) {
    if (t0 === null) t0 = now;
    var dt = last === null ? 16 : now - last;
    last = now;
    var el = (now - t0) / 1000;
    if (POINTER) {
      var pf = 1 - Math.exp(-PK * dt), mf = 1 - Math.exp(-MK * dt);
      S.px = settle(S.px, P.tx, pf); S.py = settle(S.py, P.ty, pf);
      M.x  = settle(M.x,  M.tx, mf); M.y  = settle(M.y,  M.ty, mf);
    }
    /* the wall itself is static — only the LIGHT sweeps. The only field motion
       is the (subtle) desktop cursor parallax. */
    P.x = POINTER ? S.px * PAR_X : 0;
    P.y = POINTER ? S.py * PAR_Y : 0;
    draw(el + 6.0);
    raf = requestAnimationFrame(loop);
  }

  function start() { if (ANIM && raf === null) { last = null; raf = requestAnimationFrame(loop); } }
  function stop()  { if (raf !== null) { cancelAnimationFrame(raf); raf = null; } }

  if (!initGL()) return;                       // failure => class never added

  window.__e3Obsidian = true;
  document.documentElement.classList.add("e3-obsidian-on");   // opens the windows

  lastW = window.innerWidth; lastH = window.innerHeight;

  window.addEventListener("resize", onResize);
  window.addEventListener("orientationchange", onResize);

  if (POINTER) window.addEventListener("pointermove", function (e) {
    M.tx = e.clientX * DPR;
    M.ty = (window.innerHeight - e.clientY) * DPR;
    P.tx = (e.clientX / window.innerWidth  - 0.5) * 2;
    P.ty = (e.clientY / window.innerHeight - 0.5) * 2;
  }, { passive: true });

  /* The site header is position:sticky and pins ABOVE page content, while this
     canvas sits at z-index:-1 BEHIND it. So once the page scrolls, a fully
     transparent header row would reveal the content passing underneath rather
     than the wall. There is no CSS :stuck selector, so flag the scrolled state
     here and let e3-obsidian.css lay an obsidian scrim over the pinned header. */
  var scrolled = null;
  function onScroll() {
    var s = window.scrollY > 4;
    if (s !== scrolled) {
      scrolled = s;
      document.documentElement.classList.toggle("e3-obsidian-scrolled", s);
    }
  }
  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll();

  document.addEventListener("visibilitychange", function () {
    if (document.hidden) stop(); else start();
  });

  canvas.addEventListener("webglcontextlost", function (e) {
    e.preventDefault();
    stop();
    document.documentElement.classList.remove("e3-obsidian-on");  // back to opaque navy
  });

  canvas.addEventListener("webglcontextrestored", function () {
    if (initGL()) {
      document.documentElement.classList.add("e3-obsidian-on");
      start();
    }
  });

  if (ANIM) start(); else draw(6.0);
})();
